import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface GatedContentState {
  data: Record<string, any> | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches gated Firestore content for a given app, returning data only if the user is authenticated and allowed.
 * @param {string} appId - The Firestore document ID under the `gatedContent` collection.
 * @returns {GatedContentState} The fetched data, loading flag, and any error message.
 */
export const useGatedContent = (appId: string): GatedContentState => {
  const { user, isAllowed } = useAuth();
  const [state, setState] = useState<GatedContentState>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!user || !isAllowed) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let cancelled = false;

    const fetchContent = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const snap = await getDoc(doc(db, 'gatedContent', appId));
        if (cancelled) return;
        if (snap.exists()) {
          setState({ data: snap.data(), loading: false, error: null });
        } else {
          setState({ data: null, loading: false, error: 'Content not found' });
        }
      } catch {
        if (cancelled) return;
        setState({ data: null, loading: false, error: 'Access denied' });
      }
    };

    fetchContent();
    return () => { cancelled = true; };
  }, [user, isAllowed, appId]);

  return state;
};
